import React, { useEffect, useMemo, useRef } from "react";
import { useFragment } from "react-relay";

import { faArrowRight, faChevronLeft, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { graphql } from "relay-runtime";

import {
	AutoRecordRuleDescription,
	AutoRecordRulesListItem,
} from "~/account/Copilot/AutoRecordRules/AutoRecordRulesListItem";
import { useOpenCreateModal } from "~/account/Copilot/AutoRecordRules/modal/CreateAndEdit";
import { Button } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";

import { Divider, SettingsPageTitle } from "./shared";

import type { CustomRulesBanner$key } from "~/__generated__/CustomRulesBanner.graphql";
import type { CustomRulesSettings$data, CustomRulesSettings$key } from "~/__generated__/CustomRulesSettings.graphql";

import styles from "./CustomRules.module.css";
import meetingCopilotStyles from "./MeetingCopilot.module.css";

interface CustomRulesBannerProps {
	viewer: CustomRulesBanner$key;
	onNavigateConfigure: () => void;
	showCustomRules?: boolean;
}

export function ViewAndManageCustomRulesBannerLink({ onNavigateConfigure }: { onNavigateConfigure: () => void }) {
	return (
		<a className={styles.customRulesBannerLink} onClick={onNavigateConfigure}>
			View and manage
			<FontAwesomeIcon icon={faArrowRight} />
		</a>
	);
}

export function CustomRulesBanner({ viewer: viewerRef, onNavigateConfigure, showCustomRules }: CustomRulesBannerProps) {
	const fragment = graphql`
		fragment CustomRulesBanner on Viewer {
			autoRecordRules {
				ruleLevel
			}
		}
	`;

	const { autoRecordRules } = useFragment<CustomRulesBanner$key>(fragment, viewerRef);

	const { customRules, workspaceRules } = useMemo(() => {
		let customRules = [];
		let workspaceRules = [];
		for (const rule of autoRecordRules) {
			if (rule.ruleLevel === "USER") {
				customRules.push(rule);
			} else {
				workspaceRules.push(rule);
			}
		}
		return { customRules, workspaceRules };
	}, [autoRecordRules]);

	const message = useMemo(() => {
		const customCount = customRules.length;
		const workspaceCount = workspaceRules.length;
		const workspaceRuleText = workspaceCount === 1 ? "policy is" : "policies are";
		const customRuleText = customCount === 1 ? "rule" : "rules";

		if (!showCustomRules) {
			return workspaceCount > 0
				? `${workspaceCount} workspace ${workspaceRuleText} active.`
				: "No workspace policies are currently active.";
		}

		if (customCount > 0 && workspaceCount > 0) {
			return `${customCount} custom ${customRuleText} and ${workspaceCount} workspace ${workspaceRuleText} active.`;
		} else if (customCount > 0) {
			return `${customCount} custom ${customRuleText} active.`;
		} else if (workspaceCount > 0) {
			return `0 custom rules and ${workspaceCount} workspace ${workspaceRuleText} active.`;
		} else {
			return "Currently, no meetings will be automatically recorded.";
		}
	}, [customRules.length, showCustomRules, workspaceRules.length]);

	// Show the view and manage custom rules banner link if the user currently has custom rules setup.
	return (
		<div className={styles.customRulesBannerContainer}>
			{message}
			{showCustomRules && <ViewAndManageCustomRulesBannerLink onNavigateConfigure={onNavigateConfigure} />}
		</div>
	);
}

interface CustomRulesSettingsProps {
	navigateBack: () => void;
	viewer: CustomRulesSettings$key;
	refetch: () => void;
	showCustomRules?: boolean;
	hasAdminAccess: boolean;
}

export function CustomRulesSettings({
	navigateBack,
	viewer: viewerRef,
	refetch,
	showCustomRules,
	hasAdminAccess,
}: CustomRulesSettingsProps) {
	const fragment = graphql`
		fragment CustomRulesSettings on Viewer {
			account {
				...CreateAndEditModalAccount
				...AutoRecordRulesListItemWorkspacePill
				...AutoRecordRulesListItemRuleDescriptionTooltips
			}
			autoRecordRules {
				...AutoRecordRulesListItemRule

				id
				ruleType
				ruleLevel
				filterAttendeeType
				filterMinimumAttendees
				filterMaximumAttendees
				filterKeywordsContains
				filterKeywordsRestrict
				filterOrganizer

				...AutoRecordRulesListItemAppliesTo
				...AutoRecordRulesListItemRuleDeletionConfirmModal
				...AutoRecordRulesListItemDuplicateAutoRecordRule
				...CreateAndEditModalRule
			}
		}
	`;

	const { autoRecordRules, account } = useFragment<CustomRulesSettings$key>(fragment, viewerRef);

	const customRules = autoRecordRules.filter(rule => rule.ruleLevel === "USER");
	const workspaceRules = autoRecordRules.filter(rule => rule.ruleLevel === "WORKSPACE" || rule.ruleLevel === "GROUP");
	const { open: openAutoRecordRuleModal, isOpen: isAutoRecordRuleModalOpen } = useOpenCreateModal(
		"RECORD",
		refetch,
		account,
		"USER",
	);

	// If the user hasn't made any rules for themselves, we want to immediately open the modal to help them get started.
	// However, there are also some cases where we don't want to open the modal:
	const previouslyOpenedModal = useRef(false); // If the user makes a rule when opening this page and then deletes it, we don't want to open the modal again.
	const numberOfRulesOnFirstLoad = useRef(customRules.length); // Also don't open the modal if the user already had a rule on first load and then deleted it.

	useEffect(() => {
		if (
			showCustomRules &&
			!isAutoRecordRuleModalOpen &&
			numberOfRulesOnFirstLoad.current === 0 &&
			customRules.length === 0 &&
			!previouslyOpenedModal.current
		) {
			openAutoRecordRuleModal();
			previouslyOpenedModal.current = true;
		}
	}, [showCustomRules, customRules, isAutoRecordRuleModalOpen, openAutoRecordRuleModal, previouslyOpenedModal]);

	return (
		<div className={meetingCopilotStyles.main}>
			<SettingsPageTitle>
				<div className={styles.customRulesTitle}>
					<IconButton icon={faChevronLeft} onClick={navigateBack} iconSize="xs" />
					Recording Rules
				</div>
			</SettingsPageTitle>
			<div>
				<div className={styles.defaultSectionTitle}>Your custom recording rules</div>
				<div className={styles.defaultSectionSubtitle}>
					Configure the type of meetings the Note Taker will automatically request to join and record.
				</div>
				<div className={styles.rulesetList}>
					{showCustomRules && (
						<>
							{customRules.map((rule: CustomRulesSettings$data["autoRecordRules"]["0"]) => (
								<AutoRecordRulesListItem key={rule.id} rule={rule} account={account} refetch={refetch} secondary />
							))}
						</>
					)}
					{!showCustomRules && (
						<div className={styles.ruleListItem}>
							<span>
								Change your auto-record setting to <b>Meetings following my custom policies</b> to add and enable your
								custom policies.{" "}
								<a className={styles.link} onClick={navigateBack}>
									Change setting.
								</a>
							</span>
						</div>
					)}
					<Button variant="placeholder" onClick={openAutoRecordRuleModal} disabled={!showCustomRules}>
						<div className={styles.addButtonContent}>
							<FontAwesomeIcon icon={faPlus} />
							Add a policy
						</div>
					</Button>
				</div>
			</div>
			<div className={styles.dividerContainer}>
				<Divider />
			</div>
			<div>
				<div className={styles.defaultSectionTitle}>Workspace recording policies</div>
				<div className={styles.defaultSectionSubtitle}>
					In addition to your policies, your workspace configured the following recording policies:
				</div>
				<div className={styles.rulesetList}>
					{workspaceRules.length > 0 &&
						workspaceRules.map((rule: CustomRulesSettings$data["autoRecordRules"]["0"]) => (
							<div key={rule.id} className={styles.ruleListItem}>
								<AutoRecordRuleDescription rule={rule} className={styles.mutedRule} account={account} />
							</div>
						))}

					{workspaceRules.length === 0 && (
						<div className={styles.noWorkspaceRulesMessage}>
							<span>No workspace policy currently apply to you.</span>
							{hasAdminAccess && (
								<span>
									As a workspace admin, you can{" "}
									<a href="/account/copilot/recording-rules/">configure workspace policies from workspace settings.</a>
								</span>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
